import { positionKey } from "../chess/positionKey.ts";
import { lossFromLines, rankLines, type RankedLine } from "./medals.ts";

/**
 * Engine scores for the moves of positions the student has faced, so the
 * coach can drop a suggestion the engine refutes. Static checks (exchanges,
 * mate in one) cannot see forks, trapped pieces or counterattacks; the
 * engine's multi-line search can. Positions without scores are coached from
 * the static checks alone.
 */
type EngineView = { lines: RankedLine[]; complete: boolean };

const views = new Map<string, EngineView>();

/** A suggestion may cost at most this much (centipawns) against the engine's best move. */
export const ENGINE_MAX_LOSS = 60;
/** A capture the coach says wins material must be close to the engine's best move. */
export const ENGINE_CLAIM_LOSS = 15;
/** The depth of the search that scores a position's moves. */
export const VET_DEPTH = 12;

export function rememberEngineLines(fen: string, lines: RankedLine[], legalCount: number): void {
  if (lines.length < 2 && legalCount > 1) return;
  if (views.size > 300) views.clear();
  views.set(positionKey(fen), { lines, complete: lines.length >= legalCount });
}

export function forgetEngineLines(): void {
  views.clear();
}

export function hasEngineLines(fen: string): boolean {
  return views.has(positionKey(fen));
}

/** The scored lines for a position, when a search has already stored them. */
export function engineLinesFor(fen: string): RankedLine[] | null {
  return views.get(positionKey(fen))?.lines ?? null;
}

/**
 * The engine's verdict on a move: true when it costs at most
 * ENGINE_MAX_LOSS, false when it costs more (or was not among the lines of
 * a partial search, which lists the best moves first), and undefined when
 * the position has no engine scores.
 */
export function engineApproves(
  fen: string,
  move: { from: string; to: string; promotion?: string },
  maxLoss = ENGINE_MAX_LOSS
): boolean | undefined {
  const view = views.get(positionKey(fen));
  if (!view) return undefined;
  const loss = lossFromLines(view.lines, `${move.from}${move.to}${move.promotion || ""}`);
  return loss != null && loss <= maxLoss;
}

/** How much a move costs against the engine's best, in centipawns; null when unknown. */
export function engineLoss(
  fen: string,
  move: { from: string; to: string; promotion?: string }
): number | null {
  const view = views.get(positionKey(fen));
  if (!view) return null;
  return lossFromLines(view.lines, `${move.from}${move.to}${move.promotion || ""}`);
}

/** The engine's best moves in the position, best first, as UCI strings. */
export function engineBestMoves(fen: string): string[] {
  const view = views.get(positionKey(fen));
  if (!view) return [];
  return rankLines(view.lines)
    .filter((line) => (lossFromLines(view.lines, line.uci) ?? Infinity) <= ENGINE_MAX_LOSS)
    .map((line) => line.uci);
}
